import React, { useState } from 'react'

import { CheckIcon, CopyIcon } from 'lucide-react'
import { formatUnits } from 'viem'

import {
  CurrencyAmount,
  NetworkDisplay,
  NetworkLogo,
  Skeleton,
  TokenDisplayView,
  Tooltip,
  type CLSize,
} from '~/components/component-library'
import { InlinePositionIcon } from '~/components/component-library/TokenDisplay'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import {
  TokenMetadataWithPrice,
  useTokenMetadata,
} from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { useVaultAssets } from '~/hooks/useTokens'
import { HIDDEN_DIGITS } from '~/store/privacyModeStore'
import { IAddress } from '~/types'
import { isStablecoin } from '~/utils/addresses'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { cn } from '~/utils/tailwind-utils'
import { getVaultAssetData } from '~/utils/vault'

const STABLECOIN_DEPEGGED_THRESHOLD = 0.005

/**
 * Props for the TokenBalance component.
 */
export interface TokenBalanceProps {
  /**
   * Token contract address (e.g., "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2" for WETH on mainnet)
   * Must be a valid 20-byte hex address, not a token symbol.
   */
  address: IAddress

  /**
   * Chain ID where the token exists (e.g., 1 for mainnet, 8453 for Base)
   */
  chainId: number

  /**
   * The amount of tokens in smallest unit as a bigint
   * - For ETH: amount in wei (e.g., parseUnits('1.5', 18) for 1.5 ETH)
   * - For USDC: amount in smallest unit (e.g., parseUnits('100', 6) for 100 USDC)
   */
  tokenAmount: bigint

  /**
   * Determines the layout style:
   * - "standard": Displays as a block with USD value above token amount (most common)
   * - "inline": Displays inline within text, shows as "$X.XX (Y TOKEN)" with trailing zeros removed
   */
  layout: 'standard' | 'inline'

  /**
   * Text size variant:
   * - "small": Smaller text for compact displays
   * - "base": Standard text size
   */
  size: CLSize

  /**
   * Transaction direction (⚠️ standard layout only):
   * - "inflow": When funds are entering an account (green text, plus sign)
   * - "outflow": When funds are leaving an account (minus sign)
   * - "neutral": All other cases (swaps, at-rest balances)
   *
   * Note: Bridges are represented as two separate transactions - an outflow on the source chain
   * and an inflow on the destination chain.
   *
   * ⚠️ Important: The direction prop cannot be used with inline layout - it will throw an error.
   */
  direction: 'inflow' | 'outflow' | 'neutral'

  /**
   * Optional USD value to display instead of calculating from token price
   * - When provided, this value will be used instead of automatic price calculation
   * - The value will still go through the same formatting pipeline (formatUsdValue)
   * - Useful for displaying custom USD values or handling edge cases
   */
  usdValue?: number

  /**
   * Whether the USD value is currently loading
   * - When true, shows a skeleton loader in place of the USD value
   * - The token amount will still be displayed normally
   * - Only affects the USD value display, not the token amount
   */
  isLoadingUsdValue?: boolean

  /**
   * Whether to show the network icon in the second line (⚠️ standard layout only)
   * - When true, displays NetworkLogo on the right side of the token amount line
   * - Cannot be used with inline layout (throws an error)
   */
  showNetworkIcon?: boolean

  /**
   * Replace the token symbol with a copy icon (⚠️ standard layout only)
   * - Copies the token amount to clipboard when clicked
   * - Use only when the token symbol is displayed elsewhere (e.g. next to a TokenDisplay)
   * - Cannot be used with inline layout (throws an error)
   */
  enableCopyAmount?: boolean
}

/** Format unabbreviated token amounts (8 decimal places) */
const formatUnabbreviatedTokenAmount = (amount: number): string => {
  if (amount === 0) {
    return '0.00000000'
  }

  if (amount < 0.00000001) {
    return '<0.00000001'
  }

  // Round to 8 decimal places
  const rounded = Math.round(amount * 100000000) / 100000000

  // Format with commas for large numbers
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  }).format(rounded)
}

/** Format abbreviated token amounts based on token type */
export const formatAbbreviatedTokenAmount = (
  amount: number,
  token: { address: string; chainId: number },
  options: { removeTrailingZeros?: boolean } = {},
): string => {
  const { removeTrailingZeros = false } = options
  const { address, chainId } = token
  const _isStablecoin = isStablecoin(address, chainId)

  if (amount === 0) {
    return _isStablecoin ? '0.00' : '0'
  }

  // Handle stablecoins
  if (_isStablecoin) {
    if (amount < 0.01) {
      return '<0.01'
    }

    if (amount < 1000) {
      // Mimic USD formatting for stablecoins under 1000
      const rounded = Math.round(amount * 100) / 100
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: removeTrailingZeros ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(rounded)
    }
  } else {
    // Handle non-stablecoins
    if (amount < 0.001) {
      return '<0.001'
    }

    if (amount < 1) {
      // Round to 3 decimal places
      const rounded = Math.round(amount * 1000) / 1000
      if (removeTrailingZeros) {
        return rounded.toString()
      }
      return rounded.toFixed(3)
    }
  }

  // For amounts >= 1 (non-stable) or >= 1000 (stable), use 3 significant digits
  const { value, suffix } = getAbbreviatedNumber(amount)

  // Format to 3 significant digits
  let formatted: string
  if (value >= 100) {
    formatted = Math.round(value).toString()
  } else if (value >= 10) {
    formatted = (Math.round(value * 10) / 10).toFixed(1)
  } else {
    formatted = (Math.round(value * 100) / 100).toFixed(2)
  }

  return `${formatted}${suffix}`
}

/** Get abbreviated number with suffix (K, M, B, T) */
const getAbbreviatedNumber = (
  num: number,
): { value: number; suffix: string } => {
  if (num >= 1e12) return { value: num / 1e12, suffix: 'T' }
  if (num >= 1e9) return { value: num / 1e9, suffix: 'B' }
  if (num >= 1e6) return { value: num / 1e6, suffix: 'M' }
  if (num >= 1e3) return { value: num / 1e3, suffix: 'K' }
  return { value: num, suffix: '' }
}

interface ParsedTokenBalanceProps extends TokenBalanceProps {
  decimals: number
  symbol: string
  usdValue?: number
  isPosition: boolean
  formattedTokenAmount: string
  preciseTokenAmount: string
  decimalTokenAmount: number
  formattedTooltipAmount: string
  /** Exact decimal amount copied by enableCopyAmount */
  copyAmount: string
}

const parseTokenBalanceProps = (
  props: TokenBalanceProps,
  deps: {
    tokenMetadata: TokenMetadataWithPrice | undefined
    convertTokenToUSD: ReturnType<typeof useTokenPrices>['convertTokenToUSD']
    vaultAssets: bigint | undefined
    isLoadingVaultAssets: boolean
  },
): ParsedTokenBalanceProps => {
  const { tokenAmount, address, chainId } = props
  const {
    tokenMetadata,
    convertTokenToUSD,
    vaultAssets,
    isLoadingVaultAssets,
  } = deps

  const isInline = props.layout === 'inline'
  let amountToUse = isInline && tokenAmount < 0n ? -tokenAmount : tokenAmount
  const usdValueToUse =
    props.usdValue && isInline ? Math.abs(props.usdValue) : props.usdValue
  let asset = address
  let vaultAssetDecimals: number | undefined

  const isPosition = tokenMetadata?.isPosition ?? false

  if (isPosition) {
    const vaultAssetData = getVaultAssetData(chainId, address)
    // Keep the original tokenAmount if vault assets are still loading to prevent flashing $0.00
    amountToUse = vaultAssets ?? (isLoadingVaultAssets ? tokenAmount : 0n)
    // The loading fallback is in vault-share units, so the underlying asset
    // and decimals only apply to it when shares match underlying decimals
    // (aTokens); formatting 18-decimal wrapper shares as 6-decimal underlying
    // would inflate the displayed amount and USD value ~10^12x.
    const amountIsInUnderlyingUnits =
      vaultAssets !== undefined ||
      vaultAssetData?.decimals === tokenMetadata?.decimals
    if (amountIsInUnderlyingUnits) {
      asset = vaultAssetData?.asset ?? address
      vaultAssetDecimals = vaultAssetData?.decimals
    }
  }

  if (!tokenMetadata || !tokenMetadata.validToken) {
    return {
      ...props,
      decimals: 0,
      symbol: 'UNKNOWN',
      usdValue: usdValueToUse,
      isPosition: false,
      formattedTokenAmount: '0',
      preciseTokenAmount: '0',
      decimalTokenAmount: 0,
      formattedTooltipAmount: '0',
      copyAmount: '0',
    }
  }

  const { symbol, price } = tokenMetadata
  const decimals = vaultAssetDecimals ?? tokenMetadata.decimals

  // Single conversion from on-chain units; display parses it, copy uses it as is
  const decimalAmount = formatUnits(amountToUse, decimals)
  const decimalTokenAmount = parseFloat(decimalAmount)

  const _isStablecoin = isStablecoin(props.address, props.chainId)

  let calculatedUsdValue = convertTokenToUSD({
    token: asset,
    amount: amountToUse,
    chainId,
  })

  if (!calculatedUsdValue && price) {
    calculatedUsdValue = decimalTokenAmount * price
  }

  // Optimistically price stablecoins 1:1 while the real price loads; the
  // fetched price replaces this on arrival, so a depeg still shows through
  if (calculatedUsdValue === undefined && _isStablecoin && !isPosition) {
    calculatedUsdValue = decimalTokenAmount
  }

  const safeUsdValue = usdValueToUse ?? calculatedUsdValue

  const preciseTokenAmount = formatUnabbreviatedTokenAmount(decimalTokenAmount)
  const formattedTokenAmount = formatAbbreviatedTokenAmount(
    decimalTokenAmount,
    { address: asset, chainId },
    { removeTrailingZeros: isInline },
  )

  // Stablecoins and positions show 2 decimals, other tokens 8. It's a token
  // amount shown beside the symbol, so no currency sign.
  const formattedTooltipAmount =
    _isStablecoin || isPosition
      ? decimalTokenAmount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : preciseTokenAmount

  return {
    ...props,
    decimals,
    symbol,
    usdValue: safeUsdValue,
    isPosition,
    formattedTokenAmount,
    preciseTokenAmount,
    decimalTokenAmount,
    formattedTooltipAmount,
    copyAmount: decimalAmount,
  }
}

const useParsedTokenBalancePropsFromMetadata = (
  props: TokenBalanceProps,
  tokenMetadata: TokenMetadataWithPrice | undefined,
): ParsedTokenBalanceProps => {
  const { tokenAmount, address, chainId } = props
  const isInline = props.layout === 'inline'
  const amountToUse = isInline && tokenAmount < 0n ? -tokenAmount : tokenAmount
  const isPosition = tokenMetadata?.isPosition ?? false

  const { convertTokenToUSD } = useTokenPrices()
  const { data: vaultAssets, isLoading: isLoadingVaultAssets } = useVaultAssets(
    {
      vault: address,
      chainId,
      balance: amountToUse,
      enabled: isPosition,
    },
  )

  return parseTokenBalanceProps(props, {
    tokenMetadata,
    convertTokenToUSD,
    vaultAssets,
    isLoadingVaultAssets,
  })
}

/**
 * Pure-presentation variant: caller supplies the already-fetched token
 * metadata, so the component avoids the per-instance useTokenMetadata call.
 * Use in lists that batch metadata at the parent (e.g. TokenSelector).
 */
export const TokenBalanceView: React.FC<
  TokenBalanceProps & { tokenMetadata: TokenMetadataWithPrice | undefined }
> = ({ tokenMetadata, ...props }) => {
  const parsedProps = useParsedTokenBalancePropsFromMetadata(
    props,
    tokenMetadata,
  )

  // Handle undefined token metadata
  if (!parsedProps.symbol) {
    return (
      <div className="flex flex-col items-end justify-center">
        <div className="text-sm text-secondary">Invalid token</div>
      </div>
    )
  }

  // Throw error if direction is used with inline layout
  if (parsedProps.layout === 'inline' && parsedProps.direction !== 'neutral') {
    throw new Error(
      'TokenBalance: direction prop cannot be used with inline layout',
    )
  }

  // Throw error if showNetworkIcon is used with inline layout
  if (parsedProps.layout === 'inline' && parsedProps.showNetworkIcon) {
    throw new Error(
      'TokenBalance: showNetworkIcon prop cannot be used with inline layout',
    )
  }

  // Throw error if enableCopyAmount is used with inline layout
  if (parsedProps.layout === 'inline' && parsedProps.enableCopyAmount) {
    throw new Error(
      'TokenBalance: enableCopyAmount prop cannot be used with inline layout',
    )
  }

  if (parsedProps.layout === 'inline') {
    return <TokenBalanceInline {...parsedProps} />
  }

  return <TokenBalanceStandard {...parsedProps} />
}

/**
 * TokenBalance component for displaying token balances with USD values.
 *
 * Provides a consistent way to display token balances throughout the app.
 * Automatically fetches token metadata and calculates USD values based on
 * the provided token address and chain.
 *
 * @param {TokenBalanceProps} props - The component props
 * @param {string} props.address - Token contract address
 * @param {number} props.chainId - Chain ID where the token exists
 * @param {bigint} props.tokenAmount - The amount of tokens in smallest unit (e.g., wei for ETH)
 * @param {"standard" | "inline"} props.layout - Determines the layout style (inline removes trailing zeros)
 * @param {"inflow" | "outflow" | "neutral"} props.direction - Transaction direction (⚠️ standard layout only)
 * @param {"small" | "base"} props.size - Text size variant
 * @param {boolean} [props.showNetworkIcon] - Whether to show network icon in token amount line (⚠️ standard layout only)
 */
export const TokenBalance: React.FC<TokenBalanceProps> = (props) => {
  const tokenMetadata = useTokenMetadata({
    chainId: props.chainId,
    address: props.address,
    includePrice: true,
  })
  return <TokenBalanceView {...props} tokenMetadata={tokenMetadata} />
}

/** Helper function to check if a stablecoin is depegged (more than half a cent per dollar) */
const isDepegged = (usdValue: number, tokenAmount: number): boolean => {
  if (tokenAmount === 0) return false
  // USD values are signed based on direction, so we need to take the absolute value
  const pricePerToken = Math.abs(usdValue) / tokenAmount
  return Math.abs(1 - pricePerToken) > STABLECOIN_DEPEGGED_THRESHOLD
}

/** Inline layout component */
const TokenBalanceInline: React.FC<ParsedTokenBalanceProps> = ({
  formattedTokenAmount,
  isPosition,
  isLoadingUsdValue,
  usdValue,
  decimalTokenAmount,
  address,
  chainId,
  symbol,
}) => {
  // Show loading if explicitly loading or if USD value is undefined (still fetching)
  const isLoading = isLoadingUsdValue || usdValue === undefined
  const isPrivacyModeOn = usePrivacyMode()

  /**  whether to show only the token symbol or include the token amount. */
  const shouldUseSimplifiedFormat = React.useMemo(
    () =>
      usdValue !== undefined &&
      (isStablecoin(address, chainId) || isPosition) &&
      !isDepegged(usdValue, decimalTokenAmount),
    [decimalTokenAmount, address, chainId, isPosition, usdValue],
  )

  const symbolNode = (
    <TokenDisplayView
      layout="inline-text"
      address={address}
      chainId={chainId}
      symbol={symbol}
      isPosition={isPosition}
    />
  )

  /**
   * Helper function to render token information (symbol only or token amount + symbol).
   * Eliminates code duplication between different rendering branches.
   */
  const renderTokenInfo = () => {
    if (shouldUseSimplifiedFormat) {
      // Simplified: Just show symbol (e.g., "USDC")
      return <> {symbolNode}</>
    }

    // Non-simplified: Show token amount + symbol (e.g., "(0.5 WETH)")
    return (
      <>
        {' '}
        ({isPrivacyModeOn ? HIDDEN_DIGITS : formattedTokenAmount} {symbolNode})
      </>
    )
  }

  return (
    <span>
      {isLoading ? (
        <Skeleton
          className="inline-block h-5 flex-shrink-0 align-middle"
          length={2}
        />
      ) : (
        <>
          <CurrencyAmount value={usdValue} layout="inline" />
          {renderTokenInfo()}
        </>
      )}
    </span>
  )
}

/** Standard layout component */
const TokenBalanceStandard: React.FC<ParsedTokenBalanceProps> = (props) => {
  return (
    <TokenBalanceContainer>
      <CurrencyAmount
        value={props.usdValue}
        layout="standard"
        size={props.size}
        isLoading={props.isLoadingUsdValue}
        direction={props.direction}
      />
      <TokenBalanceTokenAmount {...props} />
    </TokenBalanceContainer>
  )
}

/** Container for standard layout */
export const TokenBalanceContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex max-w-44 flex-col items-end justify-center gap-0.5">
    {children}
  </div>
)

/** Token amount component with tooltip */
const TokenBalanceTokenAmount: React.FC<
  ParsedTokenBalanceProps & {
    className?: string
  }
> = ({
  className,
  formattedTokenAmount,
  symbol,
  isPosition,
  size,
  formattedTooltipAmount,
  copyAmount,
  ...props
}) => {
  const [isCopied, setIsCopied] = useState(false)
  const isPrivacyModeOn = usePrivacyMode()

  // Manually truncate if too long (tooltip prevents truncation)
  const numSymbolCharsToShow = 10
  const displaySymbol =
    symbol.length > numSymbolCharsToShow
      ? symbol.slice(0, numSymbolCharsToShow) + '...'
      : symbol
  const needsSymbolTooltip = symbol !== displaySymbol

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    copyToClipboard(copyAmount, 'Copied amount')
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  return (
    <Tooltip
      content={
        <span className="whitespace-nowrap">
          {isPrivacyModeOn ? HIDDEN_DIGITS : formattedTooltipAmount} {symbol}
          {isPosition && <InlinePositionIcon />} on{' '}
          <NetworkDisplay layout="inline-text" chainId={props.chainId} />
        </span>
      }
      delay={200}
    >
      <div
        onClick={props.enableCopyAmount ? handleCopy : undefined}
        className={cn(
          'flex items-center gap-1 text-secondary',
          {
            'h-3 text-xs leading-4':
              props.layout === 'standard' && size === 'small',
            'h-4 text-sm leading-4':
              props.layout === 'standard' && size === 'base',
            'cursor-pointer': props.enableCopyAmount,
          },
          className,
        )}
      >
        <span className="flex items-center truncate">
          <span className="flex-shrink-0">
            {isPrivacyModeOn ? HIDDEN_DIGITS : formattedTokenAmount}
          </span>
          {props.enableCopyAmount ? (
            <div className="ml-1 text-secondary">
              {isCopied ? (
                <CheckIcon className="h-3 w-3" />
              ) : (
                <CopyIcon className="h-3 w-3" />
              )}
            </div>
          ) : (
            <span
              className="ml-1 max-w-16 truncate"
              title={needsSymbolTooltip ? symbol : undefined}
            >
              <TokenDisplayView
                layout="inline-text"
                address={props.address}
                chainId={props.chainId}
                symbol={symbol}
                isPosition={isPosition}
              />
            </span>
          )}
        </span>
        {props.showNetworkIcon && (
          <NetworkLogo
            chainId={props.chainId}
            size={size === 'small' ? 'xs' : 'sm'}
          />
        )}
      </div>
    </Tooltip>
  )
}

/** Formatters for TokenBalance component */
export const tokenBalanceFormatters = {
  formatUnabbreviatedTokenAmount,
  formatAbbreviatedTokenAmount,
}

export const __testing__ = {
  parseTokenBalanceProps,
}
